// Packages
import { datadogRum } from '@datadog/browser-rum';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// Constants
import { APP_HERO_PRODUCTS, BOOKING_STATUSES } from '@white-label-configuration/constants';

// Types
import type {
  ManageBookingParsedOrderItem,
  ManageBookingParsedState,
} from '@white-label-types/stores';

// Helpers
import {
  getBookingBaggageDetails,
  getBookingItemPiiData,
  getManageBookingState,
} from '@white-label-helper/api-manage-booking';
import { createDiscountSummaryProp } from '@white-label-helper/discount-summary-prop-creator';
import { cancelBookingHelper } from '@white-label-helper/pusher-cancel-booking';
import { convertTimeObjToString } from '@white-label-helper/time-helpers';
import { PiiData } from '@white-label-types/parking-booking';

// Stores
import useDepositStore from '@white-label-store/deposits';

export const useManageBookingStore = defineStore('manage-booking', () => {
  const manageBookingState = ref<ManageBookingParsedState | null>(null);

  const showFields = ref({
    licensePlate: true,
  })

  const isUpdatingBookingDetails = ref(false);

  const readStringEntryTime = computed(() => {
    if (manageBookingState.value?.entryExitInfo) {
      const { entryDate, entryTime } = manageBookingState.value.entryExitInfo;
      return convertTimeObjToString({ date: entryDate, time: entryTime });
    }
    return '';
  });

  const readStringExitTime = computed(() => {
    if (manageBookingState.value?.entryExitInfo) {
      const { exitDate, exitTime } = manageBookingState.value.entryExitInfo;
      return convertTimeObjToString({ date: exitDate, time: exitTime });
    }
    return '';
  });

  const readBooking = computed(() => ({
    ...manageBookingState.value,
    entryDateTime: readStringEntryTime.value,
    exitDateTime: readStringExitTime.value,
    cancellationPolicies: manageBookingState.value?.cancellationPolicies,
    amendmentPolicies: manageBookingState.value?.amendmentPolicies,
    amendmentFee: manageBookingState.value?.amendmentFee,
  }))

  const readBundle = computed(() => manageBookingState.value?.bundle);
  const readId = computed(() => manageBookingState.value?.id);
  const readAmendItemId = computed(() => manageBookingState.value?.amendItemId);
  const readPiiToken = computed(() => manageBookingState.value?.piiToken);
  const readPiiData = computed(() => manageBookingState.value?.piiData);
  const readBookingBaggageConfig = computed(() => manageBookingState.value?.bookingBaggageConfig);
  const readUserInfo = computed(() => manageBookingState.value?.user);
  const readUserEmail = computed(() => manageBookingState.value?.user?.email);
  const readEntryExitInfo = computed(() => manageBookingState.value?.entryExitInfo);
  const readOrderId = computed(() => manageBookingState.value?.orderId);
  const readOrderItemReference = computed(() => manageBookingState.value?.orderItemReference);
  const readCancellationProtection = computed(() => manageBookingState.value?.cancellationProtection);
  const readManageBookingToken = computed(() => manageBookingState.value?.token);
  const readManageBookingItems = computed(() => manageBookingState.value?.items);
  const readInventoryName = computed(() => manageBookingState.value?.inventoryItem?.display_name);
  const readInventoryItem = computed(() => manageBookingState.value?.inventoryItem);
  const readAirportName = computed(() => manageBookingState.value?.locationInfo?.name);
  const readAirportCode = computed(() => manageBookingState.value?.locationInfo?.code);
  const readOrderTotals = computed(() => manageBookingState.value?.totals);

  const readAmendItem = computed(() => {
    if (manageBookingState.value?.amendItemId) {
      return manageBookingState.value.items.find(
        (item) => item.id === Number(manageBookingState.value?.amendItemId)
      );
    }
    return;
  });

  const readGroups = computed(() => manageBookingState.value?.groups);
  const readBookingStatus = computed(() => {
    const status = manageBookingState.value?.status;
    return status ? BOOKING_STATUSES[status] : undefined;
  });
  const readDiscount = computed(() => createDiscountSummaryProp(manageBookingState.value));
  const readIsCancelled = computed(() => readBookingStatus.value === BOOKING_STATUSES.CANCELLED);
  const readIsConfirmed = computed(() => readBookingStatus.value === BOOKING_STATUSES.CONFIRMED);
  const readIsInProgress = computed(() => readBookingStatus.value === BOOKING_STATUSES.IN_PROGRESS);
  const readIsPastBooking = computed(() => readBookingStatus.value === BOOKING_STATUSES.PAST_BOOKING);
  const readIsAmendable = computed(() => manageBookingState.value?.isAmendable);
  const readIsCancellable = computed(() => manageBookingState.value?.isCancellable);
  const readLanguageISOCode = computed(() => manageBookingState.value?.language);
  const readProductCode = computed(() => manageBookingState.value?.productCode);

  const readAvailableAdditionalFields = computed(() => {
    const fields = manageBookingState.value?.items.reduce((acc, current) => {
      if (!acc) {
        return { ...current.inventory_item.additional_fields };
      }
      const obj = { ...acc };

      Object.keys(current.inventory_item.additional_fields).forEach((key) => {
        if (!obj[key]) {
          obj[key] = current.inventory_item.additional_fields[key];
        } else {
          obj[key].is_required = obj[key].is_required || current.inventory_item.additional_fields[key].is_required;
          obj[key].is_show = obj[key].is_show || current.inventory_item.additional_fields[key].is_show;
        }
      });

      return obj;
    }, null);

    return {
      zipcode: {
        toShow: fields?.zipcode?.is_show || false,
        isRequired: fields?.zipcode?.is_required || false,
      },
      full_address: {
        toShow: fields?.full_address?.is_show || false,
        isRequired: fields?.full_address?.is_required || false,
      },
      phone_number: {
        toShow: fields?.phone_number?.is_show || false,
        isRequired: fields?.phone_number?.is_required || false,
      },
      plate_number: {
        toShow: fields?.plate_number?.is_show || false,
        isRequired: fields?.plate_number?.is_required || false,
      },
      vehicle_make: {
        toShow: fields?.vehicle_make?.is_show || false,
        isRequired: fields?.vehicle_make?.is_required || false,
      },
      vehicle_model: {
        toShow: fields?.vehicle_model?.is_show || false,
        isRequired: fields?.vehicle_model?.is_required || false,
      },
      vehicle_colour: {
        toShow: fields?.vehicle_colour?.is_show || false,
        isRequired: fields?.vehicle_colour?.is_required || false,
      },
      inbound_flight_number: {
        toShow: fields?.inbound_flight_number?.is_show || false,
        isRequired: fields?.inbound_flight_number?.is_required || false,
      },
      outbound_flight_number: {
        toShow: fields?.outbound_flight_number?.is_show || false,
        isRequired: fields?.outbound_flight_number?.is_required || false,
      },
      number_of_people_in_vehicle: {
        toShow: fields?.number_of_people_in_vehicle?.is_show ?? false,
        isRequired: fields?.number_of_people_in_vehicle?.is_required ?? false,
      },
      vehicle_roadworthy: {
        toShow: fields?.vehicle_roadworthy?.is_show ?? false,
        isRequired: fields?.vehicle_roadworthy?.is_required ?? false,
      },
      max_vehicle_weight: {
        toShow: fields?.max_vehicle_weight?.is_show ?? false,
        isRequired: fields?.max_vehicle_weight?.is_required ?? false,
      },
      terminal_name: {
        toShow: fields?.terminal_name?.is_show ?? false,
        isRequired: fields?.terminal_name?.is_required ?? false,
      },
      cruise_ship_name: {
        toShow: fields?.cruise_ship_name?.is_show ?? false,
        isRequired: fields?.cruise_ship_name?.is_required ?? false,
      },
    };
  });

  const readLeadTraveller = computed(() => {
    const travelInsuranceItem = manageBookingState.value?.items.find(
      (item) => item.product_code === 'travel_insurance'
    );

    if (!travelInsuranceItem || !travelInsuranceItem.travellers?.length) {
      return null;
    }

    return travelInsuranceItem.travellers.find(
      (traveller) => traveller.is_lead
    ) || null;
  });

  const readHasTravelInsurance = computed(() => {
    return manageBookingState.value?.items.some(
      (item) => item.product_code === 'travel_insurance'
    );
  });

  const readProductTypesCategories = computed(() => {
    const productType = manageBookingState.value?.productCode || 'parking';
    const categories: string[] = [];
    switch (productType) {
      case 'parking':
        categories.push('entry', 'exit');
        break;
      case 'lounges':
        categories.push('entry', 'guest');
        break;
      case 'travel_insurance':
        categories.push('policyNumber');
        break;
    }
    return categories;
  });

  async function dispatchState(
    params: { token: string; amendItemId?: string | null }
  ) {
    isUpdatingBookingDetails.value = false;
    try {
      const state = await getManageBookingState(params.token);
      if (params.amendItemId) {
        state.amendItemId = params.amendItemId;
      }
      if (state) {
        showFields.value = { licensePlate: state?.showLicensePlate };
        manageBookingState.value = { ...manageBookingState.value, ...state };
        const deposit = useDepositStore();
        deposit.updateCartDepositTotal({
          ...state.payable,
        });
        deposit.updateItemDeposit(state.items);
        return state;
      }

      return null;
    } catch (e) {
      const errorMessage = new Error(
        'Something when wrong setting manage booking state',
        { cause: e }
      );
      datadogRum.addError(errorMessage, {
        partner: window.location.host,
      });

      return errorMessage;
    }
  };

  async function dispatchPiiData(
    requestData: { token: string }
  ) {
    try {
      if (!manageBookingState.value) {
        console.error('Manage booking state is not set. Cannot fetch PII data.');
        return;
      }
      const bookingItems = readManageBookingItems.value;

      const loungeBookings = bookingItems?.filter(booking => booking.product_code === APP_HERO_PRODUCTS.LOUNGES) ?? [];
      const results: PromiseSettledResult<PiiData>[] = await Promise.allSettled(
        loungeBookings.map(async (bookingItem: ManageBookingParsedOrderItem) => {
          const response = await getBookingItemPiiData(
            requestData.token,
            bookingItem.id
          );
          return { ...response, bookingItemId: bookingItem.id } as PiiData;
        })
      );

      const piiData = results
        .filter((result): result is PromiseFulfilledResult<PiiData> => result.status === 'fulfilled' && result.value != null)
        .map((result) => result.value);

      const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      failures.forEach((failure) => {
        datadogRum.addError(
          new Error('Failed to fetch PII data for booking item', { cause: failure.reason }),
          { partner: window.location.host }
        );
      });

      manageBookingState.value.piiData = piiData;

      return null;
    } catch (e) {
      // Safety net for errors outside Promise.allSettled (e.g. readManageBookingItems access)
      const errorMessage = new Error(
        'Something went wrong fetching PII data',
        { cause: e }
      );
      datadogRum.addError(errorMessage, {
        partner: window.location.host,
      });

      return e;
    }
  };

  async function dispatchBaggageConfigData(
    requestData: {
      token: string;
      manageBookingToken: string;
      bookingId: number;
    }
  ) {
    if (!manageBookingState.value) {
      console.error('Manage booking state is not set. Cannot fetch baggage config data.');
      return;
    }

    try {
      manageBookingState.value.bookingBaggageConfig = null;
      const response = await getBookingBaggageDetails(
        requestData.token,
        requestData.manageBookingToken,
        requestData.bookingId
      );
      manageBookingState.value.bookingBaggageConfig = response;
      return null;
    } catch (e) {
      // Error already tracked via metric in getBookingBaggageDetails.
      // Re-throw to allow callers to handle.
      console.warn('Failed to fetch booking baggage config data', e);
      throw e;
    }
  };

  async function dispatchCancelBooking(
    payload: {
      manageBookingToken: string;
      orderReference: string;
      isMultiItem?: boolean;
    }
  ) {
    try {
      const response = await cancelBookingHelper().cancelBooking(
        payload.manageBookingToken,
        payload.orderReference
      );

      let managementToken;
      if (payload.isMultiItem) {
        const responseArr = response as unknown as Array<{
          management_token: { token: string; expiration: number };
          order_reference: string;
        }>;
        managementToken = responseArr[responseArr.length - 1]?.management_token;
      } else {
        const responseObj = response as {
          management_token: { token: string; expiration: number };
          order_reference: string;
        };
        managementToken = responseObj?.management_token;
      }
      setManageBookingToken(managementToken)
      commitCancelBooking()

      return response;
    } catch (e) {
      const errorMessage = new Error(
        'Something when wrong with booking cancellation',
        { cause: e }
      );
      datadogRum.addError(errorMessage, {
        partner: window.location.host,
      });

      return e;
    }
  };

  async function dispatchCancelBundle(
    payload: {
      manageBookingToken: string;
      bundleId: string;
      orderReference: string,
      refundAmount: string;
    }
  ) {
    try {
      const response = await cancelBookingHelper().cancelBundle(
        payload.manageBookingToken,
        payload.bundleId,
        payload.orderReference,
        payload.refundAmount,
      );

      const responseObj = response as {
        management_token: { token: string; expiration: number };
        order_reference: string;
      };

      const managementToken = responseObj?.management_token;
      setManageBookingToken(managementToken)
      commitCancelBooking()

      return response;
    } catch (e) {
      const errorMessage = new Error(
        'Something when wrong with booking cancellation',
        { cause: e }
      );
      datadogRum.addError(errorMessage, {
        partner: window.location.host,
      });

      return e;
    }
  }

  function commitManageBookingState(payload: ManageBookingParsedState) {
    manageBookingState.value = { ...manageBookingState.value, ...payload };
  }

  function commitClearManageBookingState() {
    manageBookingState.value = null;
  }

  function commitCancelBooking() {
    if (manageBookingState.value) {
      manageBookingState.value.status = BOOKING_STATUSES.CANCELLED;
    }
  }

  function setManageBookingToken(
    payload: {
      token: string;
      expiration: number;
    }
  ) {
    if (manageBookingState.value) {
      manageBookingState.value.token = payload;
    }
  }

  function commitUpdateLoungeSearchCriteriaMutation(payload: object) {
    if (manageBookingState.value) {
      manageBookingState.value.searchCriteria = {
        lounges: {
          ...manageBookingState.value.searchCriteria?.lounges,
          ...payload,
        },
      };
    }
  }

  function setIsUpdatingBookingDetails(value: boolean) {
    isUpdatingBookingDetails.value = value;
  }

  return {
    // State
    manageBookingState,
    showFields,
    isUpdatingBookingDetails,

    // Getters
    readBooking,
    readBundle,
    readId,
    readAmendItemId,
    readPiiToken,
    readPiiData,
    readBookingBaggageConfig,
    readUserInfo,
    readUserEmail,
    readEntryExitInfo,
    readOrderId,
    readOrderItemReference,
    readCancellationProtection,
    readLeadTraveller,
    readHasTravelInsurance,
    readManageBookingToken,
    readManageBookingItems,
    readInventoryName,
    readInventoryItem,
    readAirportName,
    readAirportCode,
    readOrderTotals,
    readAmendItem,
    readStringEntryTime,
    readStringExitTime,
    readGroups,
    readBookingStatus,
    readDiscount,
    readIsCancelled,
    readIsConfirmed,
    readIsInProgress,
    readIsPastBooking,
    readIsAmendable,
    readIsCancellable,
    readLanguageISOCode,
    readProductCode,
    readAvailableAdditionalFields,
    readProductTypesCategories,

    // Actions
    dispatchState,
    dispatchPiiData,
    dispatchBaggageConfigData,
    dispatchCancelBooking,
    dispatchCancelBundle,

    // Mutations
    commitManageBookingState,
    commitClearManageBookingState,
    commitCancelBooking,
    setManageBookingToken,
    commitUpdateLoungeSearchCriteriaMutation,
    setIsUpdatingBookingDetails,
  }
}, {
  // @ts-expect-error Pinia persist plugin not typed
  persist: true,
})
